/**
 * CizilenDuvarlarFormu.jsx — Proje Beyanı sihirbazının "Duvarlar" adımının gövdesi (backlog #676).
 *
 * ESKİSİ (FirmaProjelerListPage.jsx'te WallDimensionsKroki, MOCK yolunda hâlâ duruyor): her stand için aynı
 * sabit L şekilli 6 duvar. ŞİMDİ (canlı yol): standın ÇİZİMİNDEKİ her kenar için bir satır — uzunluk çizimden
 * gelir ve genişliğin başlangıç değeridir; yükseklik o kenara çizilen yükseklik, yoksa beyan edilen H Max;
 * çizimin koridora bakan dediği kenarlar işaretli ve bir AÇIKLIK (metre) alanı taşır. Bu kenarda duvar yoksa
 * (açık kenar) işaret kaldırılır, satır gönderilmez. Çizilmemiş bir stand için aynı satırlar elle yazılır.
 *
 * Kontrollü bir bileşendir: satırlar, yükleme/hata durumu ve backend ihlalleri üstten gelir; sayfa kaydeder
 * (handleSaveWalls). Kurallar ve satırın kurulması: duvarSatirlari.js.
 *
 * Standın GERÇEK ana hattı satırların üstünde çizilir (StandKrokisi): bir kenara tıklamak satırını seçer, bir satıra
 * tıklamak (ya da içindeki bir alana odaklanmak) kenarını vurgular. Ana hat yalnızca çizimin HER kenarın başlangıcını da
 * ittiği standlarda vardır (V139, backlog #676); plan fdsVector'da bir kez daha kaydedilene kadar eski standlarda köşe
 * yoktur ve adım satır listesiyle kalır (standKroki.cizimKonturu null verir, hiçbir şey tahmin edilmez).
 * WAS: "⚠️ Şeklin kendisi ÇİZİLMEZ. Backend kenarın uzunluğunu bilir, köşe koordinatını değil ... şeklin kendisi köşe
 *      noktaları da itildiğinde gelir." — köşe noktaları artık itiliyor.
 */
import React from "react";
import { Alert, Badge, Button, Input, Spinner, Table } from "reactstrap";
import { Info, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import KuralIhlalListesi, { KuralIhlalSatiri } from "./KuralIhlalListesi";
import StandKrokisi from "./StandKrokisi";
import { cizimKonturu } from "./standKroki";
import {
  aciklikYuzdesi, aciklikZorunluMu, ihlalleriSatirlaraDagit, numaralar, satirSorunlari, sorunluSatirlar,
} from "./duvarSatirlari";

export default function CizilenDuvarlarFormu({
  veri, yukleniyor, hata, satirlar, onDegis, onEkle, onSil, ihlaller, limitMetni = null,
}) {
  const { t, i18n } = useTranslation("portal");
  const [secili, setSecili] = React.useState(null);
  const kontur = React.useMemo(() => cizimKonturu(veri), [veri]);
  const fmt = (v) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(Number(v));
  const fmtYuzde = (v) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(Number(v));

  if (yukleniyor) {
    return (
      <div className="text-center py-4 text-muted">
        <Spinner size="sm" className="me-2" />{t("projeler.cizilenDuvarlarOkunuyor")}
      </div>
    );
  }

  const cizili = Boolean(veri?.drawn) && !hata;
  const zorunlu = aciklikZorunluMu(veri);
  const minYuzde = veri?.minAciklikYuzde;
  const sayi = numaralar(satirlar);
  const sorunlu = new Set(sorunluSatirlar(satirlar, veri));
  const dagitim = ihlalleriSatirlaraDagit(satirlar, ihlaller);
  const koridorVar = satirlar.some((s) => s.tur === "cizili" && s.koridor);
  const krokiVar = cizili && kontur != null;

  // Krokide bir kenara tıklanınca satırı seçilir ve görünür kalır (uzun listede satır ekran dışında olabilir).
  const kenariSec = (key) => {
    setSecili(key);
    document.getElementById(`duvar-satir-${key}`)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  };
  const dahilSayisi = satirlar.filter((s) => s.dahil).length;

  const sutunSayisi = 6;
  const ihlalSatiri = (s, kolon) => {
    const kutu = dagitim.satirlar[s.key];
    const liste = kutu ? [...kutu.width, ...kutu.height, ...kutu.aciklik] : [];
    if (liste.length === 0) return null;
    return (
      <tr key={`${s.key}-ihlal`}>
        <td colSpan={kolon} className="pt-0 border-top-0">
          {liste.map((v, i) => <KuralIhlalSatiri key={i} violation={v} />)}
        </td>
      </tr>
    );
  };

  return (
    <>
      {/* #711: what the walls are judged against, shown BEFORE the numbers are typed: the resolved height limit with its source ("4,00 m — Ahşap standlar için", describeHeightLimit)
          and the H Max the stand declared (a wall above it is refused whatever the limit says, #678). Only what is known is shown: no limit recorded = no line, never "unlimited". */}
      {(limitMetni || veri?.declaredHMax != null) && (
        <div className="small mb-2" data-testid="duvar-limitleri">
          {limitMetni && (
            <div className="text-muted">
              <Info size={12} className="me-1" />{t("projeler.duvarYuksekligiSiniri")}{" "}<span className="fw-semibold">{limitMetni}</span>
            </div>
          )}
          {veri?.declaredHMax != null && (
            <div className="text-muted">
              <Info size={12} className="me-1" />{t("projeler.duvarBeyanEdilenHMax", { m: fmt(veri.declaredHMax) })}
            </div>
          )}
        </div>
      )}
      {hata && <Alert color="warning" className="small">{t("projeler.cizimOkunamadiElleGirin")}</Alert>}
      {!hata && !cizili && <Alert color="secondary" className="small">{t("projeler.cizimYokElleGirin")}</Alert>}
      {cizili && (
        <div className="text-muted small mb-2">
          {t("projeler.cizilenDuvarlarAciklamasi")}
          {koridorVar && (
            <div className="mt-1">
              {zorunlu
                ? t("projeler.aciklikAciklamaZorunlu", { min: fmtYuzde(minYuzde) })
                : t("projeler.aciklikAciklamaIsteBagli")}
            </div>
          )}
        </div>
      )}

      {krokiVar && (
        <div className="mb-3">
          <div className="small fw-semibold mb-1">{t("projeler.krokiBaslik")}</div>
          <StandKrokisi kontur={kontur} satirlar={satirlar} numara={sayi} secili={secili} onSecim={kenariSec} />
          <div className="text-muted small mt-1">{t("projeler.krokiAciklamasi")}</div>
        </div>
      )}

      {cizili ? (
        <Table size="sm" responsive className="align-middle mb-2">
          <thead>
            <tr className="small text-muted">
              <th style={{ width: 34 }} />
              <th style={{ width: 54 }}>{t("projeler.duvarSutunu")}</th>
              <th>{t("projeler.cizimdekiKenarSutunu")}</th>
              <th style={{ width: 120 }}>{t("projeler.genislikMSutunu")}</th>
              <th style={{ width: 120 }}>{t("projeler.yukseklikMSutunu")}</th>
              <th style={{ width: 190 }}>{t("projeler.aciklikMSutunu")}</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s) => {
              const sorun = s.dahil ? satirSorunlari(s, veri) : null;
              const yuzde = aciklikYuzdesi(s);
              const altinda = zorunlu && yuzde != null && yuzde < Number(minYuzde);
              return (
                <React.Fragment key={s.key}>
                  <tr
                    id={`duvar-satir-${s.key}`} onClick={() => setSecili(s.key)}
                    className={[s.dahil ? "" : "text-muted", krokiVar && s.key === secili ? "table-active" : ""].join(" ").trim()}
                  >
                    <td>
                      <Input
                        type="checkbox" checked={s.dahil}
                        title={s.dahil ? t("projeler.buKenardaDuvarVar") : t("projeler.buKenardaDuvarYok")}
                        aria-label={s.dahil ? t("projeler.buKenardaDuvarVar") : t("projeler.buKenardaDuvarYok")}
                        onChange={(e) => onDegis(s.key, { dahil: e.target.checked })}
                      />
                    </td>
                    <td className="fw-semibold">{s.dahil ? `${sayi[s.key]}.` : "—"}</td>
                    <td>
                      <div>
                        {t("projeler.kenarNumarasi", { n: s.edgeIndex + 1 })}
                        {veri.shapeCount > 1 && ` · ${t("projeler.sekilNumarasi", { n: s.shapeNo })}`}
                      </div>
                      <div className="small text-muted">{t("projeler.cizimdeUzunluk", { m: fmt(s.uzunlukM) })}</div>
                      {s.koridor && <Badge color="info" className="mt-1">{t("projeler.koridoraBakiyor")}</Badge>}
                    </td>
                    {s.dahil ? (
                      <>
                        <td>
                          <Input
                            type="number" min="0" step="0.1" bsSize="sm" value={s.genislik}
                            invalid={s.genislik !== "" && sorun.genislikYok}
                            onChange={(e) => onDegis(s.key, { genislik: e.target.value })}
                          />
                        </td>
                        <td>
                          <Input
                            type="number" min="0" step="0.1" bsSize="sm" value={s.yukseklik}
                            invalid={s.yukseklik !== "" && sorun.yukseklikYok}
                            onChange={(e) => onDegis(s.key, { yukseklik: e.target.value })}
                          />
                        </td>
                        <td>
                          {s.koridor ? (
                            <>
                              <Input
                                type="number" min="0" step="0.05" bsSize="sm" value={s.aciklik}
                                invalid={sorun.aciklikAralikDisi}
                                onChange={(e) => onDegis(s.key, { aciklik: e.target.value })}
                              />
                              <div className={`small ${altinda || sorun.aciklikEksik ? "text-warning" : "text-muted"}`}>
                                {yuzde != null
                                  ? `${t("projeler.aciklikKenarinYuzdesi", { pct: fmtYuzde(yuzde) })}${zorunlu ? ` · ${t("projeler.aciklikAsgari", { min: fmtYuzde(minYuzde) })}` : ""}`
                                  : (zorunlu ? t("projeler.aciklikZorunluKisa") : "")}
                              </div>
                            </>
                          ) : <span className="text-muted">—</span>}
                        </td>
                      </>
                    ) : (
                      <td colSpan={3} className="small">{t("projeler.buKenarAcikDuvarYok")}</td>
                    )}
                  </tr>
                  {ihlalSatiri(s, sutunSayisi)}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <>
          <Table size="sm" responsive className="align-middle mb-2">
            <thead>
              <tr className="small text-muted">
                <th style={{ width: 54 }}>{t("projeler.duvarSutunu")}</th>
                <th style={{ width: 150 }}>{t("projeler.genislikMSutunu")}</th>
                <th style={{ width: 150 }}>{t("projeler.yukseklikMSutunu")}</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {satirlar.map((s, i) => {
                const sorun = satirSorunlari(s, veri);
                return (
                  <React.Fragment key={s.key}>
                    <tr>
                      {/* #711: the number sent to the backend as walls.N (numaralar: the row's place among the INCLUDED rows) - the same as the position today, since a typed row is always included */}
                      <td className="fw-semibold">{`${sayi[s.key] ?? i + 1}.`}</td>
                      <td>
                        <Input
                          type="number" min="0" step="0.1" bsSize="sm" value={s.genislik}
                          invalid={s.genislik !== "" && sorun.genislikYok}
                          onChange={(e) => onDegis(s.key, { genislik: e.target.value })}
                        />
                      </td>
                      <td>
                        <Input
                          type="number" min="0" step="0.1" bsSize="sm" value={s.yukseklik}
                          invalid={s.yukseklik !== "" && sorun.yukseklikYok}
                          onChange={(e) => onDegis(s.key, { yukseklik: e.target.value })}
                        />
                      </td>
                      <td>
                        <Button
                          color="link" size="sm" className="p-0 text-danger" disabled={satirlar.length <= 1}
                          title={t("projeler.duvariSil")} aria-label={t("projeler.duvariSil")}
                          onClick={() => onSil(s.key)}
                        >
                          <Trash2 size={15} />
                        </Button>
                      </td>
                    </tr>
                    {ihlalSatiri(s, 4)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
          <Button color="secondary" outline size="sm" className="mb-2" onClick={onEkle} disabled={satirlar.length >= 50}>
            <Plus size={14} className="me-1" />{t("projeler.duvarEkle")}
          </Button>
        </>
      )}

      {cizili && dahilSayisi === 0 && (
        <Alert color="warning" className="small mb-2">{t("projeler.enAzBirKenardaDuvarOlmali")}</Alert>
      )}
      {sorunlu.size > 0 && (
        <Alert color="warning" className="small mb-2">
          {t("projeler.sorunluDuvarlarListesi", {
            list: satirlar.filter((s) => sorunlu.has(s.key)).map((s) => sayi[s.key]).join(", "),
          })}
        </Alert>
      )}

      <KuralIhlalListesi violations={dagitim.diger} title={t("projeler.duvarBilgisiKaydedilmedi")} />
    </>
  );
}
